import { Pointer } from "rfc6902";
import { Control211, Control211Props } from "../../controls/Control211";
import { useARSContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";

/**
 * Props for the ARSSelect component.
 */
export interface ARSSelectProps extends Omit<Control211Props, "value" | "setValue"> {
  /** RFC 6901 JSON Pointer path to the data in ARSContext state */
  dataPath: string;
}

/**
 * A dropdown select control that reads from and writes to ARSContext state using a JSON Pointer path.
 * This is a thin wrapper around Control211 that integrates with ARSContext.
 * Uses PATCH_ARS_STATE action to update the state value at the specified dataPath.
 *
 * @param props - Component props extending Control211Props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the data in ARSContext state.
 * @returns A React functional component rendering a Control211 integrated with ARSContext.
 */
export const ARSSelect = ({ dataPath, ...rest }: ARSSelectProps): React.ReactElement => {
  const { state, dispatch } = useARSContext();

  // Get the current value from state using the dataPath
  const pointer = Pointer.fromJSON(dataPath);
  const value = (pointer.get(state) as string) ?? "";

  /**
   * Handle selection change by dispatching PATCH_ARS_STATE action.
   *
   * @param newValue - The new value to set.
   */
  const handleChange = (newValue: string) => {
    if (newValue !== "-- None --") {
      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath,
        value: newValue,
      });
    }
  };

  return (
    <Control211
      value={value}
      setValue={handleChange}
      labelWidth={2}
      {...rest}
    />
  );
};

ARSSelect.displayName = "ARSSelect";
